import * as R from 'ramda'
import { useMemo } from 'react'
import { Room, RoomTypes } from '@weblite/ts-action/room'
import { Draft } from '@weblite/ts-action/draft'
import { Subscription } from '@weblite/ts-action/subscription'
import { useRooms, useRoomSyncStatus } from '$logic/room/react-hook'
import { useDrafts } from '$logic/draft/react-hook'
import { usePinnedRoomIds } from '$logic/user-preferences/react-hook'
import { usePendingMessages } from '$logic/pending-messages/react-hook'
import { usePendingUploads } from '$logic/pending-uploads/react-hook'
import { PendingMessage } from '$logic/pending-messages/types'
import { PendingUpload } from '$logic/pending-uploads/types'
import { useRoomsLastMessage } from '$logic/room-last-message/react-hook'
import { getRoomLastMessageSync } from '$logic/room-last-message'
import { getPrivateSpace } from '$logic/private-space'
import { useCachedUserPermissions } from '$logic/user-permissions'
import { useSubscriptions } from '$logic/subscription/react-hook'

export type RoomList = {
  chatId: string
  type?: RoomTypes
  pinned: boolean
  isOnTop?: boolean
}

function calculateListItems(
  pinnedRoomIds: string[],
  rooms: Record<string, Room>,
  pendingMessages: PendingMessage[],
  pendingUploads: PendingUpload[],
  drafts: Record<string, Draft>,
  subscriptions: Record<string, Subscription>,
) {
  const notPinnedRooms: Room[] = R.values(R.omit(pinnedRoomIds, rooms))

  const groupedPending = R.groupBy(R.prop('roomId'), [
    ...pendingMessages,
    ...pendingUploads,
  ])

  const sortedRoms = R.sortBy(
    room =>
      -R.reduce(
        R.max,
        0,
      )([
        drafts[room.id]?.date,
        getRoomLastMessageSync(room.id)?.date,
        room.createdAt,
        ...R.map(R.prop('pendingDate'), groupedPending[room.id] || []),
        room.type === 'CHANNEL' ? subscriptions[room.id]?.createdAt : -1,
      ]),
    notPinnedRooms,
  )

  const items: RoomList[] = [
    ...pinnedRoomIds.map(id => ({
      pinned: true,
      chatId: id,
      type: rooms[id]?.type,
    })),
    ...sortedRoms.map(room => ({
      pinned: false,
      chatId: room.id,
      type: room.type,
    })),
  ]
  return items
}

export let lastRoomListItems: RoomList[] = []

export function useRoomListItems(): { items: RoomList[]; loading: boolean } {
  const rooms = useRooms()
  const drafts = useDrafts()
  const pinnedRoomIds = usePinnedRoomIds()
  const pendingMessages = usePendingMessages()
  const pendingUploads = usePendingUploads()
  const lastMessages = useRoomsLastMessage()
  const roomSyncStatus = useRoomSyncStatus()
  const subscriptions = useSubscriptions()

  const items = useMemo(
    () =>
      rooms
        ? calculateListItems(
            pinnedRoomIds,
            rooms,
            pendingMessages,
            pendingUploads,
            drafts,
            subscriptions,
          )
        : [],
    [
      pinnedRoomIds,
      rooms,
      pendingMessages,
      pendingUploads,
      drafts,
      lastMessages,
    ],
  )

  lastRoomListItems = items
  return { items, loading: !items.length && !roomSyncStatus.atLeastOnce }
}

export function addPrivateSpaceToRoomsList(roomsList: RoomList[]) {
  if (!roomsList.length) return []

  const privateSpaceRoomId = getPrivateSpace()?.roomId // TODO: Do we have privateSpace at start ?
  const isPrivateSpacePinned = R.reduce(
    (acc, item) =>
      !item.pinned
        ? R.reduced(false)
        : item.chatId === privateSpaceRoomId
        ? R.reduced(true)
        : false,
    false,
    roomsList,
  )

  return privateSpaceRoomId
    ? R.prepend(
        {
          type: 'PRIVATE_SPACE',
          chatId: privateSpaceRoomId,
          pinned: isPrivateSpacePinned,
          isOnTop: true,
        },
        roomsList,
      )
    : roomsList
}

export function useRoomsWithSendMessagePermission() {
  const { items: rooms, loading } = useRoomListItems()
  const permissions = useCachedUserPermissions()

  const roomsWithPrivateSpace = useMemo(
    () =>
      addPrivateSpaceToRoomsList(
        rooms.filter(room => room.type !== 'PRIVATE_SPACE'),
      ),
    [rooms],
  )

  const loadingPermissions = useMemo(
    () => !loading && !Object.keys(permissions).length,
    [permissions, loading],
  )

  const items = useMemo(
    () =>
      loading
        ? []
        : roomsWithPrivateSpace.filter(
            item => permissions?.[item.chatId]?.sendMessage,
          ),
    [roomsWithPrivateSpace, permissions, loading],
  )

  return { items, loadingItems: loading, loadingPermissions }
}
